import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/* A card lifted out of its list (Duncan 2026-10-26, the punch list: "the same pop-up feature that the subcontractor site
   has on the punchlist items. It's the same thing we do with our AR when you click on it floats up"): AR's notes
   lift-in-blur (ARPage.js), as E3 Subs ported it to its punch list. The card starts on its row and springs up toward the
   viewer while the page blurs behind it; `line` is the row, `children` unfold under it. Escape (not while typing, nor
   while something is open over it: `paused`), the ×, a click outside or a width change close it, and the focus goes
   back to `from`. `closeRef` gets its close, for settling it back after an action. Portaled to <body>.
   `fill` (Duncan 2026-10-27, a sub on the Subcontractors tab: "poping out the sub that you click on and making it larger
   in the screen"): the card grows from its row to the whole height of the window, its row's width kept; `cardStyle`
   styles the card (its background). */
const LIFT_CSS = `
  .lift { position: fixed; inset: 0; z-index: 1000; }
  .lift-back { position: absolute; inset: 0; background: color-mix(in oklch, var(--bg-page) 52%, transparent);
    -webkit-backdrop-filter: blur(7px) saturate(.9); backdrop-filter: blur(7px) saturate(.9); opacity: 0; transition: opacity .45s cubic-bezier(.3,.6,.3,1); }
  .lift.is-open .lift-back { opacity: 1; }
  .lift.is-closing .lift-back { opacity: 0; transition-duration: .32s; }
  .lift-card { position: fixed; display: flex; flex-direction: column; background: var(--bg-surface); border: 1px solid transparent; border-radius: 2px;
    outline: none; transform: translate3d(0, 0, 0) scale(1); box-sizing: border-box;
    box-shadow: 0 1px 2px oklch(0 0 0 / 0), 0 12px 24px -8px oklch(0 0 0 / 0), 0 32px 64px -14px oklch(0 0 0 / 0);
    transition: transform .55s cubic-bezier(.3,1.36,.32,1), box-shadow .55s cubic-bezier(.3,1,.35,1), border-color .3s ease, border-radius .45s ease;
    will-change: transform, box-shadow; }
  .lift.is-open .lift-card { transform: translate3d(0, var(--lift-dy, 0px), 0) scale(1.013); border-color: var(--border-default); border-radius: 14px;
    box-shadow: var(--shadow-lift); }
  .lift.is-closing .lift-card { transition: transform .36s cubic-bezier(.35,.9,.3,1), box-shadow .36s ease, border-color .25s ease, border-radius .3s ease; }
  .lift.is-fill .lift-card { transition: transform .55s cubic-bezier(.3,1.36,.32,1), height .55s cubic-bezier(.3,1,.32,1), box-shadow .55s cubic-bezier(.3,1,.35,1),
    border-color .3s ease, border-radius .45s ease; }
  .lift.is-fill.is-closing .lift-card { transition: transform .36s cubic-bezier(.35,.9,.3,1), height .36s cubic-bezier(.35,.9,.3,1), box-shadow .36s ease,
    border-color .25s ease, border-radius .3s ease; }
  .lift-tilt { position: relative; flex: 1 1 auto; display: flex; flex-direction: column; min-height: 0; border-radius: inherit; overflow: hidden; background: inherit; }
  .lift.is-open .lift-tilt { animation: liftTip .75s cubic-bezier(.32,.75,.3,1); }
  @keyframes liftTip { 0% { transform: perspective(1200px) rotateX(0deg); } 34% { transform: perspective(1200px) rotateX(1.1deg); } 100% { transform: perspective(1200px) rotateX(0deg); } }
  .lift-scroll { flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior: contain; }
  .lift-panel { display: grid; grid-template-rows: 0fr; transition: grid-template-rows .52s cubic-bezier(.3,1,.3,1); }
  .lift.is-open .lift-panel { grid-template-rows: 1fr; }
  .lift.is-closing .lift-panel { transition-duration: .34s; }
  .lift-panel > div { min-height: 0; overflow: hidden; }
  .lift-in { opacity: 0; transform: translateY(-6px); transition: opacity .35s ease .08s, transform .45s cubic-bezier(.3,1.2,.35,1) .06s; }
  .lift.is-open .lift-in { opacity: 1; transform: none; }
  .lift.is-closing .lift-in { opacity: 0; transform: translateY(-4px); transition: opacity .18s ease, transform .3s ease; }
  .lift-x { position: absolute; top: 6px; right: 8px; z-index: 2; width: 30px; height: 30px; padding: 0; border-radius: 8px; display: inline-flex;
    align-items: center; justify-content: center; color: var(--text-3); background: var(--bg-surface); border: 1px solid var(--border-default);
    box-shadow: var(--shadow-xs); cursor: pointer; opacity: 0; transform: scale(.8);
    transition: opacity .3s ease .1s, transform .35s cubic-bezier(.3,1.4,.35,1) .1s, color .15s, border-color .15s, background .15s; }
  .lift.is-open .lift-x { opacity: 1; transform: scale(1); }
  .lift-x:hover { color: var(--text-1); border-color: var(--border-strong); background: var(--bg-surface-2); }
  .lift-x:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--accent-ring); }
  @media (prefers-reduced-motion: reduce) {
    .lift-back, .lift-card, .lift-panel, .lift-in, .lift-x { transition-duration: .01ms !important; transition-delay: 0s !important; }
    .lift-tilt { animation: none !important; }
  }
`;

const LIFT_EDGE = 16;

export default function Lift({ rect, from, ariaLabel, line, children, closeRef, paused, onClosed, fill = false, cardStyle }) {
  const [state, setState] = useState('pre');
  const stateRef = useRef(state);
  stateRef.current = state;
  const closedRef = useRef(onClosed);
  closedRef.current = onClosed;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const [dy, setDy] = useState(0);
  const [vh, setVh] = useState(() => window.innerHeight);
  // It's portaled to <body>, outside the app's .app.dark: it takes the theme along (as AR's lift does).
  const [isDark] = useState(() => !!document.querySelector('.app.dark, .theme-dark'));
  const cardRef = useRef(null);
  const lineRef = useRef(null);
  const inRef = useRef(null);
  const maxH = Math.max(320, vh - 2 * LIFT_EDGE);

  // Where the card settles: filling the window from its top edge (`fill`), or by its row when it fits there, else moved
  // up (or down) into the window -- measured as it will be once unfolded, and again whenever what's inside grows (a photo
  // loading, the send-back note, an error).
  useLayoutEffect(() => {
    const place = () => {
      if (fill) { setDy(Math.round(LIFT_EDGE - rect.top)); return; }
      const h = Math.min((lineRef.current ? lineRef.current.offsetHeight : rect.height) + (inRef.current ? inRef.current.offsetHeight : 0) + 2, maxH);
      const top = Math.min(Math.max(rect.top, LIFT_EDGE), Math.max(LIFT_EDGE, vh - LIFT_EDGE - h));
      setDy(Math.round(top - rect.top));
    };
    place();
    const ro = typeof ResizeObserver === 'undefined' || fill ? null : new ResizeObserver(place);
    if (ro && inRef.current) ro.observe(inRef.current);
    return () => { if (ro) ro.disconnect(); };
  }, [rect.top, rect.height, vh, maxH, fill]);

  // Spring it open on the next frame (a timer as well: frames stop in a hidden tab), then put the focus on the card.
  useEffect(() => {
    let r2 = 0;
    const open = () => setState((st) => (st === 'pre' ? 'open' : st));
    const r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(open); });
    const fallback = setTimeout(open, 120);
    // (not when something in it already has the focus: the send-back note opens ready to type)
    const focus = setTimeout(() => {
      const card = cardRef.current;
      if (card && !card.contains(document.activeElement)) card.focus({ preventScroll: true });
    }, 40);
    return () => {
      cancelAnimationFrame(r1);
      if (r2) cancelAnimationFrame(r2);
      clearTimeout(fallback);
      clearTimeout(focus);
    };
  }, []);

  const close = useCallback(() => {
    if (stateRef.current === 'closing') return;
    setState('closing');
    setTimeout(() => {
      closedRef.current();
      // Back to what opened it, once its row shows again.
      setTimeout(() => { if (from && from.isConnected) from.focus({ preventScroll: true }); }, 0);
    }, 360);
  }, [from]);
  if (closeRef) closeRef.current = close;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || pausedRef.current) return;
      // Escape while typing (a note, a search) keeps what's typed (and the lift).
      const ae = document.activeElement;
      if (ae && (ae.tagName === 'TEXTAREA' || ae.tagName === 'INPUT' || ae.isContentEditable)) return;
      e.stopPropagation();
      close();
    };
    const w0 = window.innerWidth;
    // A width change moves the list under it: close. A height-only change (a phone's keyboard) places it again.
    const onResize = () => (window.innerWidth !== w0 ? close() : setVh(window.innerHeight));
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onResize);
    // The page stays put behind the blur (no scrollbar jump).
    const prev = { overflow: document.body.style.overflow, pad: document.body.style.paddingRight };
    const gutter = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (gutter > 0) document.body.style.paddingRight = `${gutter}px`;
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onResize);
      document.body.style.overflow = prev.overflow;
      document.body.style.paddingRight = prev.pad;
    };
  }, [close]);

  return createPortal(
    <div className={`lift${fill ? ' is-fill' : ''}${isDark ? ' theme-dark' : ''}${state === 'open' ? ' is-open' : ''}${state === 'closing' ? ' is-closing' : ''}`}
      style={{ '--lift-dy': `${dy}px` }}>
      <style>{LIFT_CSS}</style>
      <div className="lift-back" onClick={close} />
      <div ref={cardRef} className="lift-card" role="dialog" aria-modal="true" aria-label={ariaLabel} tabIndex={-1}
        style={{
          left: rect.left - 1, top: rect.top, width: rect.width + 2, maxHeight: maxH,
          ...(fill ? { height: state === 'open' ? maxH : rect.height } : null), ...cardStyle,
        }}>
        <div className="lift-tilt">
          <button type="button" className="lift-x" onClick={close} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
          <div className="lift-scroll">
            <div ref={lineRef}>{line}</div>
            <div className="lift-panel">
              <div>
                <div ref={inRef} className="lift-in">{children}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
